import {BehaviorSubject, combineLatest, combineLatest as observableCombineLatest, Observable, of} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {
    getAuthenticatedUserApplications,
    getAuthenticatedUserPreferences,
    getDefaultIntegration,
    getPendingRequests,
    getProviderApplications,
    getVendorInstitutions,
    State
} from './app.reducers';
import {DataAccessRequestModel} from './core/model/integration-models';
import {ApplicationProviderListModel} from './views/admin/application-settings/models/application-provider-list.model';
import {InstitutionsVendorListModel} from './core/model/institutional-model';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {environment} from '@env/environment';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {User} from '@app/core/model/user';
import {CustomerService} from '@app/core/service/customer.service';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {FEATURE_TYPES} from '@app/views/admin/application-settings/models/application-properties.model';
import {Integration} from '@app/views/admin/application-settings/models/integration.model';
import {DefaultSchemaName} from '@app/views/admin/application-settings/models/data-source-config.model';
import {MixpanelService} from '@app/core/service/mixpanel.service';

export const API_EXPLORER_MENU_ITEM_KEY = 'apiexplorer';

export class NavigationModel {
    public model: any[];

    constructor(private store: Store<State>,
                private authenticatedUserService: AuthenticatedUserService,
                private mixpanelService: MixpanelService
    ) {
        const pendingRequestsApps$: Observable<string[]> = this.store.pipe(select(getPendingRequests)).pipe(
            filter((pendingRequests: DataAccessRequestModel[]) => !!pendingRequests),
            map((pendingRequests: DataAccessRequestModel[]) =>
                pendingRequests.map((pendingRequest: DataAccessRequestModel) => pendingRequest.applicationGuid)
            ));

        const invalidProviderApps$: Observable<string[]> = this.store.pipe(select(getProviderApplications)).pipe(
            filter((applications: ApplicationProviderListModel[]) => !!applications),
            map((applications: ApplicationProviderListModel[]) =>
                applications
                    .filter((application: ApplicationProviderListModel) =>
                        !!application && application.isValidConnection === false && !application.isNew
                    )
                    .map((application: ApplicationProviderListModel) => application.guid)
            ), );

        const providerApplicationsBadge$: Observable<[string[], string[]]> = observableCombineLatest(
            pendingRequestsApps$,
            invalidProviderApps$
        );

        const providerApplicationsBadgeValue$: Observable<number> = providerApplicationsBadge$.pipe(
            map(([pendingRequestsApps, invalidProviderApps]: [string[], string[]]) =>
                pendingRequestsApps.concat(invalidProviderApps)
                    .filter((item, i, list) => list.indexOf(item) === i)
                    .length
            ));

        const providerApplicationsBadgeColor$: Observable<string> = invalidProviderApps$.pipe(
            map((invalidProviderApps: string[]) => (invalidProviderApps.length > 0) ? '#f44336' : '#fb8c00'));

        // Vendor badge

        const pendingRequestsInstitutions$: Observable<string[]> = this.store.pipe(select(getPendingRequests)).pipe(
            filter((pendingRequests: DataAccessRequestModel[]) => !!pendingRequests),
            map((pendingRequests: DataAccessRequestModel[]) =>
                pendingRequests.map((pendingRequest: DataAccessRequestModel) => pendingRequest.senderOrgGuid)
            ), );

        const invalidVendorInstitutions$: Observable<string[]> = this.store.pipe(select(getVendorInstitutions)).pipe(
            filter((institutions: InstitutionsVendorListModel[]) => !!institutions),
            map((institutions: InstitutionsVendorListModel[]) =>
                institutions
                    .filter((institution: InstitutionsVendorListModel) =>
                        !!institution && institution.isValidConnection === false && !institution.isNew && institution.state == IntegrationState.SYNCED
                    )
                    .map((institution: InstitutionsVendorListModel) => institution.institutionGuid)
            ), );

        const vendorInstitutionsBadge$: Observable<[string[], string[]]> = observableCombineLatest(
            pendingRequestsInstitutions$,
            invalidVendorInstitutions$
        );

        const vendorInstitutionsBadgeValue$: Observable<number> = vendorInstitutionsBadge$.pipe(
            map(([pendingRequestsApps, invalidVendorInstitutions]: [string[], string[]]) =>
                pendingRequestsApps.concat(invalidVendorInstitutions)
                    .filter((item, i, list) => list.indexOf(item) === i)
                    .length
            ));

        const vendorInstitutionsBadgeColor$: Observable<string> = invalidVendorInstitutions$.pipe(
            map((invalidVendorInstitutions: string[]) => (invalidVendorInstitutions.length > 0) ? '#f44336' : '#fb8c00'));

        const currentApp$ = combineLatest(
            this.store.pipe(select(getAuthenticatedUserApplications)),
            this.store.pipe(select(getAuthenticatedUserPreferences),
                filter(preferences => !!preferences),
                map(preferences => preferences.getPreferencesItemValue('currentApp'))
            )
        ).pipe(
            map(([vendorApps, currentAppGuid]: [Application[], string]) => vendorApps?.find((app: Application) => app.guid == currentAppGuid))
        );

        const defaultIntegrationSchemaName$: Observable<DefaultSchemaName[]> = combineLatest(
            this.store.pipe(
                select(getDefaultIntegration),
                filter((defaultIntegration: Integration) => !!defaultIntegration && !!defaultIntegration.target),
                map((defaultIntegration: Integration) => defaultIntegration.target.defaultSchemaName)
            )
        );

        const isDataAPI$: Observable<boolean> = currentApp$
            .pipe(
                map((application: Application) => !application
                        || !application.dataSourceType
                        || (application.dataSourceType === DataSourceType.DataAPI
                            && application.featureType !== FEATURE_TYPES.SSO))
            );

        const apiExplorerVisible$: Observable<boolean> = combineLatest([isDataAPI$, defaultIntegrationSchemaName$])
            .pipe(
                map(([isDataAPI, defaultIntegrationSchemaNameList]: [boolean, DefaultSchemaName[]]) =>
                    isDataAPI
                    && defaultIntegrationSchemaNameList.length > 0 && defaultIntegrationSchemaNameList[0] !== DefaultSchemaName.ONE_ROSTER_12
                )
            );

        const dataBrowsingVisible$: Observable<boolean> = this.authenticatedUserService.authenticatedUser$
            .pipe(switchMap((user: User) =>
                this.authenticatedUserService.isVendor(user)
                    ? currentApp$
                        .pipe(
                            map((application: Application) => !application
                                    || !application.dataSourceType
                                    || (application.dataSourceType && application.dataSourceType !== DataSourceType.DataAPI
                                        || application.featureType !== FEATURE_TYPES.SSO))
                        )
                    : of(true)
            ));

        const oneEdtechVisible$ = new BehaviorSubject(!CustomerService.checkIsAmplifyCustomer()).asObservable();
        const passportVisible$ = new BehaviorSubject(
            environment.etxBaseURI && CustomerService.checkIsSupportSsoAppFeature()
        ).asObservable();
        const etxEnabled$ = new BehaviorSubject(environment.etxBaseURI).asObservable();

        const _this = this;
        this.model = [

            {
                'id'   : 'applications',
                'title': 'Home',
                'type' : 'item',
                'icon' : 'home',
                'badge': {
                    'fg': '#ffffff',
                    'bg$': providerApplicationsBadgeColor$,
                    'title$': providerApplicationsBadgeValue$,
                    'show$': providerApplicationsBadgeValue$
                },
                'url'  : '/admin/applications',
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'institutions',
                'title': 'Home',
                'type' : 'item',
                'icon' : 'home',
                'badge': {
                    'fg': '#ffffff',
                    'bg$': vendorInstitutionsBadgeColor$,
                    'title$': vendorInstitutionsBadgeValue$,
                    'show$': vendorInstitutionsBadgeValue$
                },
                'url'  : '/admin/institutions',
                'permission': ['APPLICATION_ADMIN', 'SERVICE_OPERATOR']
            },
            // {
            //     'id'   : 'sso',
            //     'title': 'SSO Portal',
            //     'type' : 'item',
            //     'url'  : '/sso',
            //     'visible': etxEnabled$,
            //     'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            // },
            {
                'id'   : 'appgallery',
                'title': 'Application Gallery',
                'type' : 'item',
                'icon' : 'apps',
                'url'  : '/admin/gallery',
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'pii-console',
                'title': 'PII Governance Console',
                'type' : 'item',
                'function' : () =>
                    _this.openExternalLink(
                        CustomerService.getPIIServiceLink(),
                        'PII Governance Console'
                    ),
                'target': '_blank',
                // 'url'  : '/admin/school-passport',
                'visible': passportVisible$,
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'school-passport',
                'title': 'AppStore',
                'type' : 'item',
                'function' : () =>
                    _this.openExternalLink(
                        `${environment.etxBaseURI}/resources/#/storefronts`,
                        'AppStore'
                    ),
                'target': '_blank',
                // 'url'  : '/admin/school-passport',
                'visible': passportVisible$,
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'datasynchistory',
                'title': 'Data Sync History',
                'type' : 'item',
                'faIcon' : 'history',
                'url'  : '/admin/applicationshistory',
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'reports',
                'title': 'Data Quality',
                'type' : 'item',
                'url'  : '/admin/reports',
                'permission': ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'dashboard',
                'title': 'Dashboard',
                'type' : 'collapse',
                'icon' : 'view_quilt',
                'url'  : '/dashboard/main',
                'permission': ['SYS_ADMIN'],
                'children': [
                    {
                        'id'   : 'issues',
                        'title': 'Issues',
                        'type' : 'item',
                        'url'  : '/dashboard/main',
                        'permission': ['SYS_ADMIN'],
                    },
                    {
                        'id'   : 'ssoportal',
                        'title': 'SSO Portal',
                        'type' : 'item',
                        'url'  : '/dashboard/ssoportal',
                        'visible': etxEnabled$,
                        'permission': ['SYS_ADMIN'],
                    },
                    {
                        'id'   : 'jobs',
                        'title': 'Jobs',
                        'type' : 'item',
                        'url'  : '/admin/jobs',
                        'permission': ['SYS_ADMIN'],
                    },
                    {
                        'id'   : 'apps',
                        'title': 'Applications',
                        'type' : 'item',
                        'url'  : '/dashboard/applications',
                        'permission': ['SYS_ADMIN'],
                    },
                    {
                        'id'   : 'vendor-apps',
                        'title': 'Vendor Applications',
                        'type' : 'item',
                        'url'  : '/dashboard/vendor-applications',
                        'permission': ['SYS_ADMIN'],
                    },
                    {
                        'id'   : 'locations',
                        'title': 'Locations',
                        'type' : 'item',
                        'url'  : '/dashboard/locations',
                        'permission': ['SYS_ADMIN'],
                    }
                ]
            },
            {
                'id'   : 'databrowsing',
                'title': 'Data Browsing',
                'type' : 'collapse',
                'faIcon': 'database',
                'url'  : '/databrowsing',
                'visible': dataBrowsingVisible$,
                'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'],
                'children': [
                    {
                        'id'   : 'rostering',
                        'title': 'Rostering',
                        'type' : 'item',
                        'url'  : '/databrowsing/list/rostering',
                        'visible': dataBrowsingVisible$,
                        'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'],
                    },
                    {
                        'id'   : 'gradebook',
                        'title': 'Gradebook',
                        'type' : 'item',
                        'url'  : '/databrowsing/list/gradebook',
                        'visible': dataBrowsingVisible$,
                        'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'],
                    },
                    {
                        'id'   : 'resource',
                        'title': 'Resource',
                        'type' : 'item',
                        'url'  : '/databrowsing/list/resource',
                        'visible': dataBrowsingVisible$,
                        'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'],
                    }
                ]
            },
            {
                'id'   : API_EXPLORER_MENU_ITEM_KEY,
                'title': 'API Explorer',
                'type' : 'item',
                'url'  : '/admin/api',
                'visible': apiExplorerVisible$,
                'permission': ['APPLICATION_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'administration',
                'title': 'Administration',
                'type' : 'item',
                'faIcon': 'tools',
                'url'  : '/administrations',
                'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR', 'SYS_ADMIN']
            },
            {
                'id'   : 'vendorApplication',
                'title': 'Application Settings',
                'type' : 'item',
                'url'  : '/admin/applications/myapplication/edit',
                'permission': ['APPLICATION_ADMIN', 'SERVICE_OPERATOR']
            },
            {
                'id'   : 'systemSetting',
                'title': 'System Settings',
                'type' : 'item',
                'faIcon': 'cogs',
                'url'  : '/settings',
                'permission': ['SYS_ADMIN']
            },
            {
                'id'   : 'dataschema',
                'title': 'Data Schema',
                'type' : 'item',
                'faIcon': 'file-alt',
                'url'  : '/admin/dataschema',
                'permission': ['SYS_ADMIN']
            },
            {
                'id'   : '1edtech',
                'title': 'OneRoster Compatibility Check',
                'type' : 'item',
                'url'  : '/admin/oneroster-compatibility-check',
                'visible': oneEdtechVisible$,
                'permission': ['APPLICATION_ADMIN', 'INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR']
            }
        ];
    }

    openExternalLink(url: string, resourceName: string): void {
        this.mixpanelService.trackSSO(url, resourceName, this.authenticatedUserService.authenticatedUser);
        window.open(url, '_blank');
    }
}
